import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ChartCard} from './ChartCard';
import type {AgentTrace, ContentBlock, QueryBlock, TraceIconKey} from './dummySupervisorData';
import {
  TraceWrap,
  TraceStepRow,
  StepIcon,
  StepBody,
  StepLabel,
  StepSub,
  StepStatus,
  Spinner,
  CheckIcon,
  FailIcon,
  FailMeta,
  NestedGroup,
  GroupLabel,
  SourcesRow,
  SourcesLabel,
  SourcePill,
  ChartRevealWrap,
  StreamHeader,
  StreamPara,
  Caret,
  StreamTableWrap,
  StreamTableRow,
  ToneValue,
  QueriesPanel,
  QueriesToggle,
  QueriesBody,
  QueryBlockWrap,
  QueryHeaderRow,
  QueryDot,
  CodeBlockPre,
  EndSourcesRow,
} from './SupervisorAgentTrace.style';

export const ICONS: Record<TraceIconKey, JSX.Element> = {
  route: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <path d='M5 9l7-6 7 6M5 15l7 6 7-6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  ),
  graph: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <circle cx='6' cy='6' r='2.2' fill='currentColor' stroke='none' />
      <circle cx='18' cy='6' r='2.2' fill='currentColor' stroke='none' />
      <circle cx='12' cy='18' r='2.2' fill='currentColor' stroke='none' />
      <path d='M7.6 7.4L10.6 16M16.4 7.4L13.4 16M8.2 6h7.6' strokeLinecap='round' />
    </svg>
  ),
  code: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.2}>
      <path d='M9 6l-5 6 5 6M15 6l5 6-5 6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  ),
  db: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <path d='M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z' />
      <path d='M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6' />
      <path d='M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6' />
    </svg>
  ),
  shield: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <path d='M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z' strokeLinejoin='round' />
      <path d='M9.3 12.2l2 2 3.4-4' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  ),
  rca: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <circle cx='10' cy='10' r='6' />
      <path d='M21 21l-5.2-5.2' strokeLinecap='round' />
    </svg>
  ),
  forecast: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <path d='M3 17l6-6 4 4 7-8' strokeLinecap='round' strokeLinejoin='round' />
      <path d='M14 7h6v6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  ),
  whatif: (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
      <path d='M4 7h16M4 12h16M4 17h16' strokeLinecap='round' />
      <circle cx='9' cy='7' r='2.3' fill='currentColor' stroke='none' />
      <circle cx='16' cy='12' r='2.3' fill='currentColor' stroke='none' />
      <circle cx='11' cy='17' r='2.3' fill='currentColor' stroke='none' />
    </svg>
  ),
  spark: (
    <svg viewBox='0 0 24 24' fill='currentColor' stroke='none'>
      <path d='M12 3l1.6 4.8L18 9l-4.4 1.5L12 15l-1.6-4.5L6 9l4.4-1.2z' />
      <path d='M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z' />
    </svg>
  ),
};

const CheckSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.6}>
    <path d='M5 13l4 4L19 7' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const FailSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.6}>
    <path d='M6 6l12 12M18 6L6 18' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const ChevronSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.4}>
    <path d='M9 6l6 6-6 6' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type StepStatusValue = 'idle' | 'running' | 'done';

interface SupervisorAgentTraceProps {
  msg: {
    agentTrace?: AgentTrace;
    contentBlocks?: ContentBlock[];
    queryBlocks?: QueryBlock[];
  };
}

// Replays the "agent trace -> streaming answer -> queries panel" animation
// (modeled on the Claude-style mockup) for a single dummy supervisor
// response. Plays once per mount, i.e. once per chat message.
export const SupervisorAgentTrace: React.FC<SupervisorAgentTraceProps> = ({msg}) => {
  const agentTrace = msg.agentTrace;
  const contentBlocks = useMemo(() => msg.contentBlocks || [], [msg.contentBlocks]);
  const queryBlocks = useMemo(() => msg.queryBlocks || [], [msg.queryBlocks]);

  const flatSteps = useMemo(() => {
    const list: Array<{icon: TraceIconKey; label: string; sub?: string; resultMeta: string; workMs: number}> = [];
    (agentTrace?.groups || []).forEach((group) => list.push(...group.steps));
    return list;
  }, [agentTrace]);

  const [stepStatuses, setStepStatuses] = useState<StepStatusValue[]>(() => flatSteps.map(() => 'idle'));
  const [sourcesVisible, setSourcesVisible] = useState(false);
  const [blockVisible, setBlockVisible] = useState<boolean[]>(() => contentBlocks.map(() => false));
  const [blockTyped, setBlockTyped] = useState<string[]>(() => contentBlocks.map(() => ''));
  const [tableRowsShown, setTableRowsShown] = useState<Record<number, number>>({});
  const [queriesOpen, setQueriesOpen] = useState(false);
  const [queriesVisible, setQueriesVisible] = useState(false);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    let cancelled = false;

    const typeWords = async (text: string, blockIndex: number, fast: boolean) => {
      const words = text.split(' ');
      let current = '';
      for (let i = 0; i < words.length; i += 1) {
        if (cancelled) return;
        current += (i === 0 ? '' : ' ') + words[i];
        const snapshot = current;
        setBlockTyped((prev) => {
          const next = [...prev];
          next[blockIndex] = snapshot;
          return next;
        });
        const base = fast ? 30 : 70;
        const variance = fast ? 20 : 55;
        let delay = base + Math.random() * variance;
        if (/[.,:]$/.test(words[i])) delay += fast ? 70 : 180;
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
      }
    };

    const run = async () => {
      for (let i = 0; i < flatSteps.length; i += 1) {
        if (cancelled) return;
        setStepStatuses((prev) => {
          const next = [...prev];
          next[i] = 'running';
          return next;
        });
        // eslint-disable-next-line no-await-in-loop
        await sleep(flatSteps[i].workMs);
        if (cancelled) return;
        setStepStatuses((prev) => {
          const next = [...prev];
          next[i] = 'done';
          return next;
        });
        // eslint-disable-next-line no-await-in-loop
        await sleep(220);
      }

      if (cancelled) return;
      await sleep(150);
      setSourcesVisible(true);
      await sleep(400);

      for (let b = 0; b < contentBlocks.length; b += 1) {
        if (cancelled) return;
        const block = contentBlocks[b];
        setBlockVisible((prev) => {
          const next = [...prev];
          next[b] = true;
          return next;
        });
        if (block.type === 'header') {
          // eslint-disable-next-line no-await-in-loop
          await typeWords(block.text, b, true);
        } else if (block.type === 'para') {
          // eslint-disable-next-line no-await-in-loop
          await typeWords(block.text, b, false);
        } else if (block.type === 'table') {
          for (let r = 0; r <= block.rows.length; r += 1) {
            if (cancelled) return;
            setTableRowsShown((prev) => ({...prev, [b]: r}));
            // eslint-disable-next-line no-await-in-loop
            await sleep(160);
          }
        } else if (block.type === 'chart') {
          // eslint-disable-next-line no-await-in-loop
          await sleep(500);
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(200);
      }

      if (cancelled) return;
      // Reveal the panel but leave it collapsed — the user expands it via
      // the toggle rather than it auto-opening once ready.
      setQueriesVisible(true);
    };

    run();

    return () => {
      cancelled = true;
    };
    // Runs exactly once per mounted message; deliberately excludes deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let stepCursor = 0;

  return (
    <>
      {agentTrace && (
        <TraceWrap>
          {agentTrace.groups.map((group, gIdx) => {
            const rows = group.steps.map((step) => {
              const idx = stepCursor;
              stepCursor += 1;
              const status = stepStatuses[idx] || 'idle';
              if (status === 'idle') return null;
              return (
                <TraceStepRow key={idx} $icon={step.icon}>
                  <StepIcon $icon={step.icon}>{ICONS[step.icon]}</StepIcon>
                  <StepBody>
                    <StepLabel>{step.label}</StepLabel>
                    {step.sub && <StepSub>{step.sub}</StepSub>}
                    <StepStatus>
                      {status === 'done' ? (
                        step.outcome === 'error' ? (
                          <>
                            <FailIcon>
                              <FailSvg />
                            </FailIcon>
                            <FailMeta>{step.resultMeta}</FailMeta>
                          </>
                        ) : (
                          <>
                            <CheckIcon>
                              <CheckSvg />
                            </CheckIcon>
                            <span>{step.resultMeta}</span>
                          </>
                        )
                      ) : (
                        <>
                          <Spinner />
                          <span>Running…</span>
                        </>
                      )}
                    </StepStatus>
                  </StepBody>
                </TraceStepRow>
              );
            });

            if (rows.every((row) => row === null)) return null;

            return group.groupLabel ? (
              <NestedGroup key={gIdx}>
                <GroupLabel>{group.groupLabel}</GroupLabel>
                {rows}
              </NestedGroup>
            ) : (
              <React.Fragment key={gIdx}>{rows}</React.Fragment>
            );
          })}
        </TraceWrap>
      )}

      {/* {agentTrace && sourcesVisible && (
        <SourcesRow>
          <SourcesLabel>Data sources</SourcesLabel>
          {agentTrace.sources.map((source) => (
            <SourcePill key={source}>{source}</SourcePill>
          ))}
        </SourcesRow>
      )} */}

      {contentBlocks.map((block, i) => {
        if (!blockVisible[i]) return null;

        if (block.type === 'header') {
          return (
            <StreamHeader key={i}>
              {blockTyped[i]}
              <Caret $show={blockTyped[i] !== block.text} />
            </StreamHeader>
          );
        }
        if (block.type === 'para') {
          return (
            <StreamPara key={i}>
              {blockTyped[i]}
              <Caret $show={blockTyped[i] !== block.text} />
            </StreamPara>
          );
        }
        if (block.type === 'chart') {
          return (
            <ChartRevealWrap key={i}>
              <ChartCard chart={block.chart} />
            </ChartRevealWrap>
          );
        }
        if (block.type === 'table') {
          const shown = tableRowsShown[i] ?? 0;
          return (
            <StreamTableWrap key={i}>
              <StreamTableRow $visible $isHeader>
                <span>Driver</span>
                <span>Variance contribution</span>
              </StreamTableRow>
              {block.rows.map((row, r) => (
                <StreamTableRow key={row.label} $visible={r < shown}>
                  <span>{row.label}</span>
                  <ToneValue $tone={row.tone}>{row.value}</ToneValue>
                </StreamTableRow>
              ))}
            </StreamTableWrap>
          );
        }
        return null;
      })}

      {queryBlocks.length > 0 && (
        <QueriesPanel $visible={queriesVisible}>
          <QueriesToggle $open={queriesOpen} onClick={() => setQueriesOpen((open) => !open)}>
            <ChevronSvg />
            <span>View queries({queryBlocks.length})</span>
          </QueriesToggle>
          <QueriesBody $open={queriesOpen}>
            {queryBlocks.map((q) => (
              <QueryBlockWrap key={q.label}>
                <QueryHeaderRow>
                  <QueryDot $color={q.color} />
                  {q.label}
                </QueryHeaderRow>
                <CodeBlockPre>
                  <code>{q.code}</code>
                </CodeBlockPre>
              </QueryBlockWrap>
            ))}
            {/* {agentTrace && (
              <EndSourcesRow>
                {agentTrace.sources.map((source) => (
                  <SourcePill key={`end-${source}`}>{source}</SourcePill>
                ))}
              </EndSourcesRow>
            )} */}
          </QueriesBody>
        </QueriesPanel>
      )}
    </>
  );
};

export default SupervisorAgentTrace;
